import { useId, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface TooltipProps {
  /** Plain text. A tooltip is not a place for controls. */
  content: string
  children: ReactNode
  side?: 'top' | 'bottom'
  className?: string
}

const OPEN_DELAY_MS = 350

/**
 * A hover hint that is also a keyboard hint.
 *
 * Opens on focus as well as hover, and closes on Escape, because a tooltip
 * reachable only by mouse is a tooltip half the audience never sees. It is
 * wired with `aria-describedby`, so it supplements the control's name rather
 * than replacing it — anything essential belongs in the label instead.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const id = useId()

  const show = (immediate = false) => {
    if (timer.current) clearTimeout(timer.current)
    if (immediate) return setOpen(true)
    timer.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS)
  }

  const hide = () => {
    if (timer.current) clearTimeout(timer.current)
    setOpen(false)
  }

  return (
    <span
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => show()}
      onMouseLeave={hide}
      onFocusCapture={() => show(true)}
      onBlurCapture={hide}
      onKeyDown={(event) => event.key === 'Escape' && hide()}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {open ? (
        <span
          role="tooltip"
          id={id}
          className={cn(
            'animate-in pointer-events-none absolute left-1/2 z-(--z-dropdown) -translate-x-1/2',
            'max-w-56 rounded-md bg-ink px-2 py-1 text-xs text-surface-raised shadow-md',
            'text-center whitespace-normal',
            side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  )
}
